import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';

interface ClickAwayListenerProps {
  children: ReactNode | ReactNode[];
  className?: string;
  style?: CSSProperties;
  onClickAway: () => void;
}

const ClickAwayListener = ({ children, className, onClickAway, style }: ClickAwayListenerProps) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const hasInteractedRef = useRef(false);

  const handleInteract = () => {
    hasInteractedRef.current = true;
  };

  useEffect(() => {
    const element = targetRef.current;
    if (!element) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!element.contains(event.target as Node) && hasInteractedRef.current) {
        hasInteractedRef.current = false;
        onClickAway();
      }
    };

    const handleBlur = (event: FocusEvent) => {
      const nextFocusedElement = event.relatedTarget as HTMLElement | null;

      if (targetRef.current?.contains(nextFocusedElement)) {
        return;
      }

      if ((event.target as HTMLElement)?.matches(':disabled')) {
        return;
      }

      // Delay to allow focus transitions (like month switching)
      setTimeout(() => {
        if (document.activeElement && targetRef.current?.contains(document.activeElement)) {
          return;
        }

        if (hasInteractedRef.current) {
          hasInteractedRef.current = false;
          onClickAway();
        }
      }, 50); // Small delay to allow re-focusing inside
    };

    document.addEventListener('mousedown', handleClickOutside);
    element.addEventListener('blur', handleBlur, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      element.removeEventListener('blur', handleBlur, true);
    };
  }, [onClickAway]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={className}
      onFocus={handleInteract}
      onMouseDown={handleInteract}
      ref={targetRef}
      style={style}
      tabIndex={-1}
    >
      {children}
    </div>
  );
};

export default ClickAwayListener;
